---
title: "Liquidation and Margin Calls: How to Avoid Them"
slug: liquidation-and-margin-calls
shortTitle: "Liquidation & Margin Calls"
description: "What margin calls and stop-outs really are, how your margin level is worked out in USDT, how to estimate your own stop-out price, and the habits that keep you clear."
level: beginner
category: trading
course: beginner
order: 10
topics: [leverage-margin, risk-management]
publishedAt: 2026-10-19
updatedAt: 2026-10-19
featured: false
popular: false
cover: /academy/img/covers/liquidation-and-margin-calls.svg
faqs:
  - q: "What is the difference between a margin call and a stop-out?"
    a: "A margin call is a warning: your margin level has fallen to an alert level and the account is under strain, but nothing has been closed. A stop-out is the action that follows if things get worse: positions are closed automatically when the margin level reaches the stop-out level."
  - q: "Does the platform show a liquidation price for my position?"
    a: "No. Margin is shared across your whole account, so the point where positions are closed depends on everything you hold. The platform shows your Margin Level, Equity and Free Margin instead, and you can estimate a stop-out price yourself with the method in this lesson."
  - q: "Which position is closed first at stop-out?"
    a: "The position with the largest loss is closed first. Positions are then closed one at a time until the margin level is back above the stop-out level."
  - q: "Should I deposit more money when I get a margin alert?"
    a: "It is one option, not the only one. A deposit raises your equity, but if the trade is wrong it also gives the loss more room to grow. Decide in advance whether you would reduce the position or add funds, so you are not making that choice under pressure."
  - q: "Can a stop-out happen even if I set a stop loss?"
    a: "Yes, if the stop loss is too far away for the size of the position, or if other positions are losing at the same time. Under cross margin every open trade draws on the same equity, so size each trade so its own stop loss is reached long before the account gets near the stop-out level."
---

Few things shock a new trader more than finding a position closed that they never closed themselves. It is rarely a glitch. When losses eat into the money backing your trades, the platform first warns you and then, if nothing changes, starts closing positions. The warning is usually called a **margin call**. The forced close is a **stop-out**, also known as **liquidation**.

This lesson explains the numbers behind both, shows you how to estimate your own stop-out price, and covers the habits that keep you well clear of it. It builds on [Leverage and Margin Explained Simply](/academy/article/leverage-and-margin).

<Warning title="Leverage magnifies losses">

With leverage, a modest price move can wipe out a large share of the margin behind a position, and in fast markets losses can be bigger than you planned for. Many traders lose money trading leveraged products. Never trade with money you cannot afford to lose.

</Warning>

<CTA />

<KeyTakeaways>

- **Margin level is the number to watch.** It is your equity divided by the margin your positions use, shown as a percentage in the account bar.
- **A margin call warns; a stop-out acts.** Alerts are sent at {{MARGIN_WARNING_LEVEL}} and {{MARGIN_CRITICAL_LEVEL}}; positions are closed at {{STOP_OUT_LEVEL}}, largest loss first.
- **Your whole account backs every trade.** Margin is shared across the account, so one large loser pulls down the margin level for everything you hold.
- **Your own stop loss should always come first.** Size each position so the stop loss you chose ends a bad trade long before a stop-out could.

</KeyTakeaways>

## The three numbers behind every margin call

The account bar on the trade screen shows Equity, Open P/L, Balance, Free Margin, Used Margin and Margin Level. Three of them decide how safe you are:

- **Equity** is what the account is worth right now: your balance (plus any credit) plus the unrealized profit or loss of your open positions, including swap.
- **Used margin** is the money locked to hold your open positions. For each position it is the position's value divided by its leverage, set when you open it.
- **Margin level** compares the two as a percentage.

**Margin level = equity ÷ used margin × 100%**

**Free margin** is equity minus used margin: what is left to open new trades and to absorb losses. When positions lose money, equity falls while used margin stays the same, so the margin level drops. That slide is the whole story of a margin call.

## Margin calls: the warning stage

Brokers once phoned clients to ask for more money. Today the "call" is an alert. On {{BRAND}}, a margin alert is sent when your margin level falls to {{MARGIN_WARNING_LEVEL}}, and a more urgent one at {{MARGIN_CRITICAL_LEVEL}}.

{/* TODO: verify with platform — how margin alerts reach clients (in-platform notification, email, push) and whether both levels notify every client by default. */}

An alert closes nothing. It gives you time to act, and you have three broad choices:

1. **Reduce risk.** Close the losing position to release its margin. On desktop, Instant Trade can also close 25% or 50% of it.
2. **Add funds.** A deposit raises equity and the margin level, but it also gives a losing trade more room to keep losing.
3. **Do nothing.** If the market keeps moving against you, the next stage takes over.

<ProTip>

You do not have to wait for the platform's alerts. Under Account → Alerts you can set your own alert for when the account margin level drops to a percentage you choose, well above the platform's levels, by push, email or in the app.

</ProTip>

## Stop-out: when positions are closed for you

If the margin level keeps falling and reaches {{STOP_OUT_LEVEL}}, the stop-out begins. The platform closes the position with the largest loss first, then checks the margin level again. If it is still too low, it closes the next one, a position at a time, until the margin level recovers.

A position closed this way appears under **Closed Positions** with the reason **Liquidation**. The loss is final, even if the price turns a minute later.

Two details matter:

- **Cross margin.** {{BRAND}} uses account-wide margin: your whole equity backs every position, and there is no isolated mode where a trade can only lose its own margin. One heavy loser lowers the margin level for everything, and if closing it is not enough, other positions follow.
- **Gaps and fast markets.** Positions are closed at market prices. If the price jumps past the stop-out point, for example when a stock market reopens after a weekend, the result can be worse than the level suggests. Read the platform's terms on how a negative balance is treated.

{/* TODO: verify with platform — negative balance protection wording (a negative cash balance is written off once no positions are open). */}

## A worked example in USDT

<Example title="One Bitcoin position, from open to stop-out (illustrative prices)">

You have a balance of **1,000 USDT** and no other positions. You buy 0.1 BTC at 60,000 USDT with 10x leverage.

- Position value: 0.1 × 60,000 = **6,000 USDT**
- Used margin: 6,000 ÷ 10 = **600 USDT**
- Margin level at the start: 1,000 ÷ 600 × 100% = **166.7%**

The price falls to 57,000. The loss is (57,000 − 60,000) × 0.1 = −300 USDT. Equity is now 1,000 − 300 = 700 USDT, and the margin level is 700 ÷ 600 × 100% = **116.7%**.

Say, for this example only, that the stop-out level were 50%. The stop-out would begin when equity fell to 50% × 600 = 300 USDT, which means a loss of 1,000 − 300 = 700 USDT. On 0.1 BTC that is a price drop of 700 ÷ 0.1 = 7,000, so the estimated stop-out price is about **53,000 USDT**.

</Example>

The example ignores spread, fees and swap, which shift the real figure slightly. Now keep the same account, leverage and hypothetical 50% stop-out, and change only the size:

<CompareTable caption="Same account, three position sizes (illustrative, hypothetical 50% stop-out)">

| Position | Value | Used margin | Loss allowed | Est. stop-out price | Room from 60,000 |
| --- | --- | --- | --- | --- | --- |
| 0.05 BTC | 3,000 USDT | 300 USDT | 1,000 − 150 = 850 USDT | 60,000 − 17,000 = 43,000 | 28.3% |
| 0.10 BTC | 6,000 USDT | 600 USDT | 1,000 − 300 = 700 USDT | 60,000 − 7,000 = 53,000 | 11.7% |
| 0.15 BTC | 9,000 USDT | 900 USDT | 1,000 − 450 = 550 USDT | 60,000 − 3,667 = 56,333 | 6.1% |

</CompareTable>

Tripling the position cut the room before stop-out from about 28% to about 6%, a move that is not unusual in crypto.

## How to estimate your own stop-out price

{{BRAND}} does not display a liquidation price, because under cross margin the stop-out depends on the whole account. For a single position you can estimate it yourself:

<Steps>
<Step title="Collect your numbers">

Read **Equity** and **Used Margin** from the account bar. Then note how many USDT the position gains or loses per 1 USDT of price movement: the **Value of $1 move** the order panel showed when you opened it (0.1 for 0.1 BTC).

</Step>
<Step title="Find the equity at stop-out">

Multiply the stop-out level by your used margin. At a 50% level and 600 USDT used, that is 300 USDT.

</Step>
<Step title="Work out the loss you can absorb">

Subtract that figure from your current equity: 1,000 − 300 = 700 USDT.

</Step>
<Step title="Turn it into a price">

Divide the loss by the Value of $1 move: 700 ÷ 0.1 = 7,000. For a buy, subtract it from the current price; for a sell, add it.

</Step>
</Steps>

With several positions open this is only a rough guide, because the others move too. The platform-guide lesson [Margin, Leverage and Liquidation Price on {{BRAND}}](/academy/article/margin-leverage-liquidation-price) goes deeper.

## Habits that keep you away from stop-out

- **Decide your loss before you enter.** Switch on **TP / SL** and type the most you will accept losing into **Loss (USDT)**. Many traders keep it to 1–2% of the account; see [Position Sizing](/academy/article/position-sizing).
- **Keep total exposure modest.** The combined value of your positions compared with your equity matters most, as the table shows.
- **Do not size up just because you can.** Leverage goes up to {{MAX_LEVERAGE}}. A high setting locks less margin per trade but also lets you open a far bigger position than you meant to. Check **Value of $1 move** before you confirm.
- **Count correlated trades as one.** Buying BTC, ETH and SOL together is close to one big crypto bet, and under cross margin they all draw on the same equity.
- **Leave free margin unused.** Untouched equity absorbs normal swings before the margin level nears the alert levels.

<Warning>

Do not move a stop loss further away, or top up the account, just to keep a losing trade alive. Both turn a planned, limited loss into an open-ended one, and the stop-out is still waiting if the price keeps going.

</Warning>

## Conclusion

A margin call is the platform telling you that losses are eating into the money behind your trades; a stop-out is the platform acting on it, closing the biggest loser first. Both follow one number, your margin level, which falls as equity falls. Because {{BRAND}} uses cross margin, every position shares that number. Estimate your own stop-out point, keep position sizes small next to your equity, and let your [stop loss](/academy/article/stop-loss-take-profit), not the stop-out, end a trade that is not working.
